// SkillSelector - Content generation template selector component
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Sparkles, Check } from 'lucide-react';
import { SKILL_CATEGORIES } from '../../../services/skillService';
import './SkillSelector.css';

const flattenDefaultSkills = () => {
    const list = [];
    Object.values(SKILL_CATEGORIES).forEach((category) => {
        category.skills.forEach((skill) => {
            list.push({
                id: skill.id,
                name: skill.name,
                description: skill.description,
                group: category.name,
            });
        });
    });
    return list;
};

const SkillSelector = ({
    value,
    onChange,
    label = '',
    options = [],
    disabled = false,
    size = 'default', // 'small' | 'default' | 'large'
    direction = 'down', // 'down' | 'up'
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const skillOptions = useMemo(
        () => (Array.isArray(options) && options.length > 0 ? options : flattenDefaultSkills()),
        [options]
    );

    const selectedSkill = useMemo(
        () => skillOptions.find((option) => option.id === value) || null,
        [skillOptions, value]
    );

    const groupedOptions = useMemo(() => {
        const groups = {};
        skillOptions.forEach((option) => {
            const key = option.group || '模板';
            if (!groups[key]) groups[key] = [];
            groups[key].push(option);
        });
        return groups;
    }, [skillOptions]);

    const handleSelect = (skillId) => {
        onChange(skillId);
        setIsOpen(false);
    };

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (!e.target.closest('.skill-selector')) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const sizeClasses = {
        small: 'h-8 text-sm px-3',
        default: 'h-10 text-base px-4',
        large: 'h-12 text-lg px-5',
    };

    return (
        <div className={`skill-selector relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    w-full ${sizeClasses[size]} rounded-[14px]
                    bg-[#F7F8FA] border border-transparent
                    hover:border-blue-200 hover:bg-white
                    focus:outline-none focus:border-blue-300
                    flex items-center justify-between gap-2
                    transition-all duration-200
                `}
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    {label && <span className="text-gray-400 whitespace-nowrap">{label}</span>}
                    <span className="text-[#2563EB] font-bold truncate">
                        {selectedSkill ? selectedSkill.name : '选择模板'}
                    </span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className={`absolute ${direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'} left-auto right-0 lg:left-0 lg:right-auto z-[60] bg-[#F8FAFC] rounded-3xl border border-gray-100 shadow-2xl animate-in fade-in slide-in-from-${direction === 'up' ? 'bottom' : 'top'}-2 duration-300 w-[300px] sm:w-[360px]`}>
                    <div className="max-h-[220px] overflow-y-auto p-1.5 no-scrollbar">
                        {Object.entries(groupedOptions).map(([groupName, groupItems]) => (
                            <div key={groupName} className="mb-3 last:mb-0">
                                <div className="px-3 py-1 text-[13px] text-gray-400 mb-1">{groupName}</div>
                                <div className="grid grid-cols-2 gap-1.5 px-0.5">
                                    {groupItems.map((skill) => (
                                        <button
                                            key={skill.id}
                                            onClick={() => handleSelect(skill.id)}
                                            className={`
                                                w-full h-full flex flex-col text-left p-2.5 rounded-xl
                                                transition-all duration-200 relative
                                                ${value === skill.id ? 'bg-blue-50/50 ring-1 ring-[#3B82F6] shadow-sm' : 'bg-white shadow-sm ring-1 ring-gray-100 hover:ring-gray-200'}
                                            `}
                                        >
                                            <div className="w-full flex justify-between items-start gap-1">
                                                <div className={`text-[14px] leading-snug tracking-wide ${value === skill.id ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{skill.name}</div>
                                                {value === skill.id && (
                                                    <Check className="w-[14px] h-[14px] text-[#3B82F6] flex-shrink-0 mt-0.5" strokeWidth={3} />
                                                )}
                                            </div>
                                            <div className={`text-[11px] mt-1 flex-1 leading-snug whitespace-pre-line ${value === skill.id ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {skill.description}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SkillSelector;
